import { MouseEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GridColDef,
  gridFilteredDescendantCountLookupSelector,
  GridRenderCellParams,
  GridRowClassNameParams,
  Toolbar,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid';
import { DataGridPro, GridApiPro } from '@mui/x-data-grid-pro';
import { Box, Button } from '@mui/material';
import { isEqual } from 'lodash';
import { getTargetAttributes, updateTargetAttributes } from '@/generated/client/targetattributes/targetattributes';
import {
  MinimalTarget,
  MinimalTargetAttributeDtoValue,
  TargetAttributeDto,
  TargetAttributeDtoType,
} from '@/generated/model';
import TypeBasedEditor from './TypeBasedEditor';
import TypeBasedDisplay from './TypeBasedDisplay';
import { SearchComponent } from '../common/Search';
import GridExport from '../common/GridExport';
import './TargetAttributes.css';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useNotificationStore } from '@/stores/notificationstore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DisplayNoResult from '../common/DisplayNoResult';

export default function TargetAttributesEditor({ selectedTargets }: { selectedTargets: MinimalTarget[] | undefined }) {
  const { t } = useTranslation();
  const [originalRows, setOriginalRows] = useState<TargetAttributeDto[]>([]);
  const [rows, setRows] = useState<TargetAttributeDto[]>([]);
  const [changedRowKeys, setChangedRowKeys] = useState<Set<string>>(new Set());
  const [searchText, setSearchText] = useState<string | undefined>();
  const [updatedRowKeys, setUpdatedRowKeys] = useState<Set<string>>(new Set());
  const showNotification = useNotificationStore((state) => state.showNotification);

  async function loadTargetAttributes() {
    setChangedRowKeys(new Set());
    setUpdatedRowKeys(new Set());
    if (!selectedTargets || selectedTargets.length === 0) {
      setOriginalRows([]);
      setRows([]);
      return;
    }
    const selectedTargetIds = selectedTargets.map((target) => target.id);
    const perTargetAttributes = (await getTargetAttributes(selectedTargetIds)).data;
    const newTargetAttributes = perTargetAttributes.attributes;
    newTargetAttributes.sort((a, b) => a.priority - b.priority);
    setOriginalRows(newTargetAttributes);
    setRows(newTargetAttributes);
  }

  useEffect(() => {
    void loadTargetAttributes();
  }, [selectedTargets]);

  useEffect(() => {
    if (!searchText) {
      setRows(originalRows);
      return;
    }
    const lower = searchText.toLowerCase();
    const filtered = originalRows.filter((row) =>
      Object.values(row).some((v) => String(v).toLowerCase().includes(lower))
    );
    setRows(filtered);
  }, [searchText, originalRows]);

  function processRowUpdate(newRow: TargetAttributeDto) {
    setRows((prev) => prev.map((prevRow) => (prevRow.key === newRow.key ? newRow : prevRow)));

    const original = originalRows.find((row) => row.key === newRow.key);
    const changed = !isEqual(newRow.value, original?.value);

    setChangedRowKeys((prev) => {
      const updated = new Set(prev);
      if (changed) {
        updated.add(newRow.key);
      } else {
        updated.delete(newRow.key);
      }
      return updated;
    });

    return newRow;
  }

  async function modifyAttributes() {
    if (!selectedTargets?.length || !changedRowKeys.size) {
      return;
    }
    const selectedTargetIds = selectedTargets.map((target) => target.id);
    const changedRows = rows.filter((row) => changedRowKeys.has(row.key));
    const changedValues = changedRows.map(({ key, type, value }) => {
      switch (type) {
        case TargetAttributeDtoType['@list']: {
          const enumValue = value?.id;
          return { key: key, value: enumValue as MinimalTargetAttributeDtoValue | undefined };
        }
        case TargetAttributeDtoType['@class']: {
          const id = value?.id;
          return { key: key, value: id as MinimalTargetAttributeDtoValue | undefined };
        }
        default:
          return { key, value };
      }
    });
    await updateTargetAttributes({ targetIds: selectedTargetIds, values: changedValues });
    showNotification('success', t('targetAttributes.successfulModification'), undefined, 3);
    setUpdatedRowKeys(changedRowKeys);
    setChangedRowKeys(new Set());
  }

  const columns: GridColDef[] = [
    {
      field: 'valueName',
      headerName: t('targetAttributes.valueHeader'),
      flex: 1,
      editable: true,
      renderEditCell: (params) => <TypeBasedEditor params={params} />,
      renderCell: (params) => <TypeBasedDisplay params={params} />,
      valueGetter: (_, row: TargetAttributeDto) => {
        if (!row.value) {
          return null;
        }
        const type: TargetAttributeDtoType = row.type;
        switch (type) {
          case TargetAttributeDtoType['@list']: {
            return row.value.translation;
          }
          case TargetAttributeDtoType['@class']: {
            return row.value.brand + ' ' + row.value.type;
          }
          default:
            return row.value;
        }
      },
      valueSetter: (value, row) => {
        if (
          (row.type === TargetAttributeDtoType['@class'] || row.type === TargetAttributeDtoType['@list']) &&
          typeof value === 'string'
        ) {
          return row;
        }
        return { ...row, value: value };
      },
    },
  ];

  function getExportFilename() {
    if (selectedTargets && selectedTargets.length === 1) {
      return selectedTargets[0].name;
    } else {
      return t('targetAttributes.exportFileName');
    }
  }

  function CustomToolbar() {
    return (
      <Toolbar className={'targetattribute-toolbar'}>
        <SearchComponent
          searchText={searchText}
          setSearchText={setSearchText}
          showHistory={false}
          placeholder={t('targetAttributes.searchPlaceHolder')}
        />
        <GridExport filename={getExportFilename()} isListView={false} />
      </Toolbar>
    );
  }

  function CustomGridTreeDataGroupingCell(props: GridRenderCellParams) {
    const { id, field, row, rowNode, formattedValue } = props;
    const apiRef = useGridApiContext<GridApiPro>();
    const filteredDescendantCountLookup = useGridSelector(apiRef, gridFilteredDescendantCountLookupSelector);
    const filteredDescendantCount = filteredDescendantCountLookup[rowNode.id] ?? 0;

    const handleClick = (event: MouseEvent<HTMLDivElement>) => {
      if (rowNode.type !== 'group') {
        return;
      }
      apiRef.current.setRowChildrenExpansion(id, !rowNode.childrenExpanded);
      apiRef.current.setCellFocus(id, field);
      event.stopPropagation();
    };

    return (
      <Box sx={{ ml: rowNode.depth * 4 }}>
        <div onClick={handleClick}>
          {rowNode.type === 'group' ? (
            <div className="attribute-category">
              {rowNode.childrenExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
              <span>
                {formattedValue} ({filteredDescendantCount})
              </span>
            </div>
          ) : (
            <span>{row.translatedKey}</span>
          )}
        </div>
      </Box>
    );
  }

  const groupingColDef = {
    headerName: t('targetAttributes.keyHeader'),
    renderCell: (params: GridRenderCellParams) => <CustomGridTreeDataGroupingCell {...params} />,
    // FIXME ne legyen any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    valueFormatter: (value: any, row: TargetAttributeDto) => {
      if (row.key) {
        return value;
      }
      return translateTag(value);
    },
  };

  function translateTag(tag: string) {
    return t('targetAttributes.type.' + tag);
  }

  function getRowClass(params: GridRowClassNameParams) {
    const rowKey = params.row.key;
    if (rowKey === undefined) {
      const type = params.id.toString().split('auto-generated-row-null/')[1];
      return 'type-row type-row-' + type;
    }
    if (changedRowKeys.has(rowKey)) {
      return 'changed-row';
    }
    if (updatedRowKeys.has(rowKey)) {
      return 'updated-row';
    }
    return '';
  }

  return (
    <>
      <div className="grid-wrapper">
        <div className="target-attributes-grid">
          <DataGridPro
            rows={rows}
            columns={columns}
            disableRowSelectionOnClick
            disableColumnMenu
            disableColumnSorting
            disableDensitySelector
            disableColumnFilter
            pageSizeOptions={[100]}
            getRowId={(attribute) => attribute.key}
            treeData
            getTreeDataPath={(row) => [...row.tag, row.translatedKey]}
            groupingColDef={groupingColDef}
            defaultGroupingExpansionDepth={-1}
            processRowUpdate={processRowUpdate}
            getRowClassName={getRowClass}
            slots={{
              toolbar: CustomToolbar,
              noRowsOverlay: () => <DisplayNoResult searchText={searchText} />,
            }}
            showToolbar
            hideFooterRowCount
            sx={{
              '--DataGrid-rowBorderColor': 'transparent',
              '& .MuiDataGrid-columnHeaders': {
                display: 'none',
              },
            }}
          />
        </div>
        {changedRowKeys.size && (
          <div className="button-container">
            <Button className="targetattribute-button save-button" onClick={modifyAttributes}>
              <CheckCircleOutlineIcon className="button-icon" />
              <span className="button-text">{t('targetAttributes.button.save')}</span>
            </Button>
            <Button className="targetattribute-button cancel-button" onClick={loadTargetAttributes}>
              <CancelOutlinedIcon className="button-icon" />
              <span className="button-text">{t('targetAttributes.button.cancel')}</span>
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
